import { buildInviteEmailDesignPreviewHtml } from 'features/InviteEmailDesign/buildInviteEmailDesignPreviewHtml'
import { resolveInviteEmailDesignLayoutLabel } from 'features/InviteEmailDesign/inviteEmailDesignTemplateHelpers'
import inviteEmailDesignApi from 'services/api/inviteEmailDesign'
import useBrand from 'hooks/useBrand'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

const previewCache = new Map()

function readCachedPreview(cacheKey) {
  const cachedEntry = previewCache.get(cacheKey)

  if (!cachedEntry) {
    return null
  }

  if (typeof cachedEntry === 'string') {
    return { html: cachedEntry, layoutLabel: '' }
  }

  return cachedEntry
}

export function invalidateInviteEmailDesignPreviewCache(templateId) {
  if (templateId == null || templateId === '') {
    previewCache.clear()
    return
  }

  const prefix = `${templateId}:`
  for (const key of previewCache.keys()) {
    if (key.startsWith(prefix)) {
      previewCache.delete(key)
    }
  }
}

export function useInviteEmailDesignPreview(
  templateId,
  { enabled = true, refreshKey = 0 } = {}
) {
  const userName = useSelector(s => s.login.currentUser?.name || '')
  const { logoUrl: brandDefaultLogoUrl } = useBrand()
  const normalizedTemplateId = templateId == null ? '' : String(templateId)
  const cacheKey = `${normalizedTemplateId}:${userName}:${refreshKey}`
  const cachedPreview = readCachedPreview(cacheKey)
  const [previewHtml, setPreviewHtml] = useState(() => cachedPreview?.html || null)
  const [layoutLabel, setLayoutLabel] = useState(() => cachedPreview?.layoutLabel || '')
  const [isLoading, setIsLoading] = useState(
    Boolean(enabled && normalizedTemplateId && !cachedPreview)
  )
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    if (!enabled || !normalizedTemplateId) {
      setPreviewHtml(null)
      setLayoutLabel('')
      setIsLoading(false)
      setHasError(false)
      return
    }

    const cachedEntry = readCachedPreview(cacheKey)
    if (cachedEntry) {
      setPreviewHtml(cachedEntry.html || null)
      setLayoutLabel(cachedEntry.layoutLabel || '')
      setIsLoading(false)
      setHasError(false)
      return
    }

    let isCancelled = false
    setPreviewHtml(null)
    setLayoutLabel('')
    setIsLoading(true)
    setHasError(false)

    inviteEmailDesignApi
      .getTemplate({ templateId: normalizedTemplateId })
      .then(emailData => {
        if (isCancelled) {
          return
        }

        const html = buildInviteEmailDesignPreviewHtml(
          emailData,
          userName,
          brandDefaultLogoUrl
        )
        const resolvedLayoutLabel = resolveInviteEmailDesignLayoutLabel(emailData)

        previewCache.set(cacheKey, {
          html,
          layoutLabel: resolvedLayoutLabel
        })
        setPreviewHtml(html || null)
        setLayoutLabel(resolvedLayoutLabel)
        setHasError(!html)
      })
      .catch(() => {
        if (!isCancelled) {
          setHasError(true)
          setPreviewHtml(null)
          setLayoutLabel('')
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [brandDefaultLogoUrl, cacheKey, enabled, normalizedTemplateId, refreshKey, userName])

  return { previewHtml, layoutLabel, isLoading, hasError }
}
